import { useState, useEffect, useMemo, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  LogIn, Sun, Moon, User, LogOut, Settings, KeyRound, ShieldCheck,
  AlertCircle, Shield, ChevronDown, Menu, X, MonitorSmartphone, LayoutDashboard,
  Loader2,
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import type { AuthProvider as AuthProviderType } from '../hooks/useAuth'
import { useTheme } from '../hooks/useTheme'
import { getMyAvatarBlob, getPreferences } from '../api/client'
import { shouldRenderNavbar } from './navbarVisibility'

function isFederated(p?: AuthProviderType): boolean { return p === 'GOOGLE' || p === 'APPLE' }
function providerLabel(p?: AuthProviderType): string {
  if (p === 'GOOGLE') return 'Google'
  if (p === 'APPLE') return 'Apple'
  return ''
}

type NavItem = {
  to: string
  label: string
  icon: typeof Shield
  show: boolean
  end?: boolean
}

export default function Navbar() {
  const { user, loading } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState('')
  const [navDisplay, setNavDisplay] = useState<'avatar' | 'initials' | 'name'>('avatar')
  const accountTriggerRef = useRef<HTMLButtonElement>(null)
  const accountMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!user) return
    let cancelled = false
    getPreferences().then((p) => {
      if (!cancelled && p?.navbarDisplay) setNavDisplay(p.navbarDisplay as 'avatar' | 'initials' | 'name')
    }).catch(() => {})
    const onPrefsUpdated = (e: Event) => {
      const d = (e as CustomEvent).detail
      if (d?.navbarDisplay) setNavDisplay(d.navbarDisplay as 'avatar' | 'initials' | 'name')
    }
    window.addEventListener('mms:prefsUpdated', onPrefsUpdated)
    return () => { cancelled = true; window.removeEventListener('mms:prefsUpdated', onPrefsUpdated) }
  }, [user])

  const isPrivileged = useMemo(() => {
    const roles = user?.roles || []
    return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_MODERATOR') || roles.includes('ROLE_MANAGER')
  }, [user])

  const roleLabel = useMemo(() => {
    const roles = user?.roles || []
    if (roles.includes('ROLE_ADMIN')) return 'Administrator'
    if (roles.includes('ROLE_MANAGER')) return 'Manager'
    if (roles.includes('ROLE_MODERATOR')) return 'Moderator'
    return null
  }, [user])

  const initials = useMemo(() => {
    const fn = (user?.firstName || '').trim()
    const ln = (user?.lastName || '').trim()
    const a = fn ? fn[0] : ''
    const b = ln ? ln[0] : ''
    if (a || b) return (a + b).toUpperCase()
    const un = (user?.username || user?.email || '').trim()
    return un ? un[0].toUpperCase() : '?'
  }, [user])

  useEffect(() => {
    let revokeUrl: string | undefined
    if (!user) { setAvatarUrl(''); return }
    const loadAvatar = () => {
      getMyAvatarBlob()
        .then((blob) => {
          if (revokeUrl) URL.revokeObjectURL(revokeUrl)
          const url = URL.createObjectURL(blob)
          revokeUrl = url
          setAvatarUrl(url)
        })
        .catch(() => setAvatarUrl(''))
    }
    loadAvatar()
    const onAvatarUpdated = () => loadAvatar()
    window.addEventListener('mms:avatarUpdated', onAvatarUpdated)
    return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl); window.removeEventListener('mms:avatarUpdated', onAvatarUpdated) }
  }, [user])

  // Close user menu on Escape or outside click
  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        accountTriggerRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) return
    requestAnimationFrame(() => accountMenuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus())
  }, [menuOpen])

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(accountMenuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [])
    if (!items.length) return
    const current = items.indexOf(document.activeElement as HTMLElement)
    let next = current
    if (event.key === 'ArrowDown') next = (current + 1) % items.length
    else if (event.key === 'ArrowUp') next = (current - 1 + items.length) % items.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = items.length - 1
    else return
    event.preventDefault()
    items[next]?.focus()
  }

  useEffect(() => {
    setMenuOpen(false)
    setMobileOpen(false)
  }, [location.pathname, location.hash])

  // Navbar stays focused on daily paths; the Admin console supplies the
  // secondary navigation for its configuration surfaces.
  const navItems: NavItem[] = [
    { to: '/',            label: 'My Access', icon: Shield,      show: Boolean(user), end: true },
    { to: '/admin/users', label: 'Admin',     icon: LayoutDashboard, show: isPrivileged },
  ]

  // Keep the member landing page intentionally minimal, but retain the signed
  // user shell anywhere an ordinary member manages their own account.
  if (!shouldRenderNavbar(Boolean(user), isPrivileged, location.pathname)) return null

  return (
      <nav className="nav-surface sticky top-0 z-40">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <div className="flex h-16 items-center gap-4">
            {/* Brand */}
            <Link
              to="/"
              className="flex items-center gap-2.5 rounded-lg pr-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--nav-ring)/0.6)]"
              aria-label="MMS home"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600 text-white shadow-sm">
                <Shield size={19} strokeWidth={2.25} />
              </span>
              <span className="hidden text-base font-semibold text-[rgb(var(--nav-fg))] sm:inline">MMS</span>
            </Link>

            {/* Divider */}
            <span className="hidden h-6 w-px bg-[rgb(var(--nav-border))] md:block" aria-hidden="true" />

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto">
              {navItems.filter((n) => n.show).map((item) => {
                const Icon = item.icon
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `nav-link${isActive ? ' is-active' : ''}`}
                  >
                    <Icon size={16} strokeWidth={2} className="nav-link__icon" />
                    {item.label}
                  </NavLink>
                )
              })}
            </div>

            <div className="flex items-center gap-1 ml-auto">
              {/* Theme toggle */}
              <button
                type="button"
                onClick={toggleTheme}
                className="nav-icon-btn"
                aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              >
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </button>

              {/* User menu */}
              {loading ? (
                <div className="flex h-10 w-10 items-center justify-center text-[rgb(var(--nav-fg-muted))]" role="status" aria-label="Checking account session" title="Checking account session">
                  <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                </div>
              ) : user ? (
                <div className="relative">
                  <button
                    ref={accountTriggerRef}
                    type="button"
                    onClick={() => setMenuOpen((o) => !o)}
                    className="nav-avatar-btn"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-label={user.email ? `Account menu for ${user.email}` : 'Account menu'}
                  >
                    {navDisplay === 'name' ? (
                      <span className="max-w-36 truncate px-2 text-sm font-medium text-[rgb(var(--nav-fg))]">
                        {user.firstName || user.username}
                      </span>
                    ) : navDisplay === 'initials' ? (
                      <span className="flex h-8 w-8 items-center justify-center rounded-full border border-brand-600/25 bg-brand-600/10 text-xs font-semibold text-brand-700 dark:text-brand-300">
                        {initials}
                      </span>
                    ) : avatarUrl ? (
                      <span className="h-8 w-8 overflow-hidden rounded-full ring-1 ring-[rgb(var(--nav-border))]">
                        <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                      </span>
                    ) : (
                      <span className="flex h-8 w-8 items-center justify-center rounded-full border border-brand-600/25 bg-brand-600/10 text-xs font-semibold text-brand-700 dark:text-brand-300">
                        {initials}
                      </span>
                    )}
                    <ChevronDown
                      size={14}
                      className={`text-[rgb(var(--nav-fg-muted))] transition-transform duration-150 ${menuOpen ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    />
                  </button>

                  {menuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setMenuOpen(false)}
                        aria-hidden="true"
                      />
                      <div
                        ref={accountMenuRef}
                        role="menu"
                        aria-label="Account"
                        onKeyDown={handleMenuKeyDown}
                        className="absolute right-0 z-20 mt-2 w-72 origin-top-right overflow-hidden rounded-lg border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-1))] shadow-xl"
                      >
                        <div className="border-b border-[rgb(var(--border-subtle))] px-4 py-3.5">
                          <p className="text-sm font-semibold truncate text-[rgb(var(--text-primary))]">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs truncate mt-0.5 text-[rgb(var(--text-muted))]">{user.email}</p>
                          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                            {isFederated(user.provider) && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                <ShieldCheck size={9} /> {providerLabel(user.provider)}
                              </span>
                            )}
                            {user.emailVerified === false && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                <AlertCircle size={9} /> Unverified
                              </span>
                            )}
                            {user.twoFactorEnabled && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400">
                                <ShieldCheck size={9} /> 2FA
                              </span>
                            )}
                            {roleLabel && <span className="chip chip--neutral">{roleLabel}</span>}
                          </div>
                        </div>
                        <div className="p-1.5">
                          <MenuLink icon={User} label="Profile" to="/settings#profile" />
                          <MenuLink icon={KeyRound} label="Sign-in & security" to="/settings#security" />
                          <MenuLink icon={MonitorSmartphone} label="Device sessions" to="/settings#sessions" />
                          <MenuLink icon={Settings} label="Preferences" to="/settings#preferences" />
                        </div>
                        <div className="border-t border-[rgb(var(--border-subtle))] p-1.5">
                          <Link
                            to="/signout"
                            onClick={() => setMenuOpen(false)}
                            className="flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            role="menuitem"
                          >
                            <LogOut size={16} /> Sign out
                          </Link>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <NavLink
                  to="/signin"
                  className={({ isActive }) => `nav-link${isActive ? ' is-active' : ''}`}
                >
                  <LogIn size={16} strokeWidth={2} className="nav-link__icon" />
                  Sign in
                </NavLink>
              )}

              {/* Mobile menu toggle */}
              {user && (
                <button
                  type="button"
                  onClick={() => setMobileOpen((o) => !o)}
                  className="nav-icon-btn md:hidden"
                  aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
                  aria-expanded={mobileOpen}
                >
                  {mobileOpen ? <X size={16} /> : <Menu size={16} />}
                </button>
              )}
            </div>
          </div>

          {/* Mobile menu */}
          {mobileOpen && (
            <div className="md:hidden pb-3 border-t border-[rgb(var(--nav-border))] pt-2 flex flex-col gap-0.5">
              {navItems.filter((n) => n.show).map((item) => {
                const Icon = item.icon
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `nav-link w-full justify-start${isActive ? ' is-active' : ''}`}
                    onClick={() => setMobileOpen(false)}
                  >
                    <Icon size={16} strokeWidth={2} className="nav-link__icon" />
                    {item.label}
                  </NavLink>
                )
              })}
              {!user && (
                <NavLink
                  to="/signin"
                  className={({ isActive }) => `nav-link w-full justify-start${isActive ? ' is-active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <LogIn size={16} strokeWidth={2} className="nav-link__icon" />
                  Sign in
                </NavLink>
              )}
            </div>
          )}
        </div>
      </nav>
  )
}

function MenuLink({
  icon: Icon,
  label,
  to,
}: {
  icon: typeof Shield
  label: string
  to: string
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      className="flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-sm text-[rgb(var(--text-secondary))] transition-colors hover:bg-[rgb(var(--nav-hover-bg)/0.7)] hover:text-[rgb(var(--text-primary))] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <Icon size={16} className="text-[rgb(var(--text-muted))]" />
      <span className="flex-1 text-left">{label}</span>
    </Link>
  )
}
